import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, KeyRound, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import SEO from "../components/SEO";
import GoogleIdentityButton from "../components/GoogleIdentityButton";
import { useConsumerAuth } from "../context/ConsumerAuthContext";
import consumerApi from "../lib/consumerApi";


const fieldClass = "w-full rounded border border-white/15 bg-white/5 px-3 py-2.5 text-sm outline-none focus:border-[var(--cc-gold)]";

function apiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join(" ");
  return fallback;
}

function AccessShell({ title, description, icon: Icon = ShieldCheck, children }) {
  return (
    <section className="min-h-[70vh] px-6 py-14" aria-labelledby="consumer-access-title">
      <div className="mx-auto w-full max-w-md">
        <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-md bg-[var(--cc-gold)] text-[var(--cc-navy)]">
          <Icon size={22} aria-hidden="true" />
        </span>
        <h1 id="consumer-access-title" className="font-display text-3xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-[var(--cc-text-muted)]">{description}</p>
        <div className="mt-8 border-t border-white/10 pt-7">{children}</div>
        <p className="mt-6 text-xs leading-relaxed text-[var(--cc-text-muted)]">
          Need help with your account?{" "}
          <a className="text-[var(--cc-gold)] hover:underline" href="mailto:support@competitionchecker.com">
            support@competitionchecker.com
          </a>
        </p>
      </div>
    </section>
  );
}

export function ConsumerLogin() {
  const { account, googleLogin, login, ready } = useConsumerAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const createGoogleChallenge = useCallback(async () =>
    (await consumerApi.post("/consumer/auth/google/challenge")).data, []);
  const finishGoogle = async (challengeId, credential) => {
    const signedIn = await googleLogin(challengeId, credential, false);
    if (signedIn) navigate("/account");
    else toast.message("Check your email to verify this account, then sign in again.");
  };
  if (ready && account) return <Navigate to="/account" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await login(identifier, password);
      navigate("/account");
    } catch (error) {
      toast.error(apiError(error, "Sign in failed."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AccessShell title="Sign in" description="Access your saved Competition Checker preferences." icon={LockKeyhole}>
      <SEO title="Sign in" description="Sign in to your Competition Checker account." path="/signin" noIndex />
      <form onSubmit={submit} className="space-y-5" data-testid="consumer-login-form">
        <label className="block text-sm font-medium">Username or email<input className={`${fieldClass} mt-2`} type="text" autoComplete="username" required value={identifier} onChange={(e) => setIdentifier(e.target.value)} /></label>
        <label className="block text-sm font-medium">Password<input className={`${fieldClass} mt-2`} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        <button className="btn-gold w-full rounded py-3 text-sm disabled:opacity-60" disabled={busy}>{busy ? "Signing in..." : "Sign in"}</button>
      </form>
      <div className="mt-6 border-t border-white/10 pt-5"><GoogleIdentityButton createChallenge={createGoogleChallenge} onCredential={finishGoogle} /></div>
      <div className="mt-6 flex flex-wrap justify-between gap-3 text-sm"><Link className="text-[var(--cc-gold)]" to="/forgot-password">Forgot password?</Link><Link className="text-[var(--cc-gold)]" to="/register">Create account</Link><Link className="text-[var(--cc-gold)]" to="/resend-verification">Resend verification</Link></div>
    </AccessShell>
  );
}

export function ConsumerRegister() {
  const { googleLogin } = useConsumerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    terms_accepted: false,
    privacy_notice_acknowledged: false,
    service_analytics: false,
    advertising_personalisation: false,
    marketing_email: false,
    anonymous_usage_insight_sharing: false,
  });
  const [sent, setSent] = useState(false);
  const createGoogleChallenge = useCallback(async () =>
    (await consumerApi.post("/consumer/auth/google/challenge")).data, []);
  const finishGoogle = async (challengeId, credential) => {
    const signedIn = await googleLogin(challengeId, credential, true);
    if (signedIn) navigate("/account");
    else setSent(true);
  };
  const update = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const submit = async (event) => {
    event.preventDefault();
    try {
      await consumerApi.post("/consumer/auth/register", form);
      setSent(true);
    } catch (error) {
      toast.error(apiError(error, "Account creation failed."));
    }
  };
  if (sent) return <AccessShell title="Check your email" description="If the address can be registered, verification instructions have been sent." icon={Mail}><Link className="text-sm text-[var(--cc-gold)]" to="/signin">Return to sign in</Link></AccessShell>;
  return (
    <AccessShell title="Create your account" description="Choose a username and use a strong password of at least 12 characters.">
      <SEO title="Create account" description="Create a Competition Checker account." path="/register" noIndex />
      <form onSubmit={submit} className="space-y-5" data-testid="consumer-register-form">
        <label className="block text-sm font-medium">Username<input className={`${fieldClass} mt-2`} type="text" autoComplete="username" minLength={3} maxLength={30} pattern="[A-Za-z][A-Za-z0-9._-]{2,29}" required value={form.username} onChange={(e) => update("username", e.target.value)} /></label>
        <label className="block text-sm font-medium">Email<input className={`${fieldClass} mt-2`} type="email" autoComplete="email" required value={form.email} onChange={(e) => update("email", e.target.value)} /></label>
        <label className="block text-sm font-medium">Password<input className={`${fieldClass} mt-2`} type="password" autoComplete="new-password" minLength={12} required value={form.password} onChange={(e) => update("password", e.target.value)} /></label>
        <CheckRow checked={form.terms_accepted} onChange={(value) => update("terms_accepted", value)} required>I agree to the <Link className="text-[var(--cc-gold)]" to="/terms">Terms of Use</Link>.</CheckRow>
        <CheckRow checked={form.privacy_notice_acknowledged} onChange={(value) => update("privacy_notice_acknowledged", value)} required>I have read the <Link className="text-[var(--cc-gold)]" to="/privacy-policy">Privacy Notice</Link>.</CheckRow>
        <button className="btn-gold w-full rounded py-3 text-sm">Create account</button>
      </form>
      <div className="mt-6 border-t border-white/10 pt-5">
        <p className="mb-3 text-sm text-[var(--cc-text-muted)]">Or create an account with Google. Optional privacy choices stay off and can be changed in your account.</p>
        {form.terms_accepted && form.privacy_notice_acknowledged ?
          <GoogleIdentityButton createChallenge={createGoogleChallenge} onCredential={finishGoogle} /> :
          <p className="text-sm text-[var(--cc-text-muted)]">Accept the Terms and acknowledge the Privacy Notice above to continue with Google.</p>}
      </div>
      <p className="mt-6 text-sm text-[var(--cc-text-muted)]">Already registered? <Link className="text-[var(--cc-gold)]" to="/signin">Sign in</Link></p>
    </AccessShell>
  );
}

function CheckRow({ checked, onChange, required = false, children }) {
  return <label className="flex items-start gap-3 text-sm leading-relaxed"><input className="mt-1 h-4 w-4 accent-[var(--cc-gold)]" type="checkbox" checked={checked} required={required} onChange={(e) => onChange(e.target.checked)} /><span>{children}</span></label>;
}

export function ConsumerForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    await consumerApi.post("/consumer/auth/forgot-password", { email });
    setSent(true);
  };
  return <AccessShell title="Reset your password" description={sent ? "If an eligible account exists, reset instructions have been sent." : "Enter your email and we will send reset instructions when the account is eligible."} icon={KeyRound}>{sent ? <Link className="text-sm text-[var(--cc-gold)]" to="/signin">Return to sign in</Link> : <form onSubmit={submit} className="space-y-5"><label className="block text-sm font-medium">Email<input className={`${fieldClass} mt-2`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label><button className="btn-gold w-full rounded py-3 text-sm">Send reset instructions</button></form>}</AccessShell>;
}

function fragmentToken() {
  const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") ||
    window.__CC_CONSUMER_ACTION_TOKEN__ || "";
  if (token && window.location.hash) {
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  }
  return token;
}

export function ConsumerResendVerification() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    try {
      await consumerApi.post("/consumer/auth/resend-verification", { email });
      setSent(true);
    } catch (error) {
      toast.error(apiError(error, "Verification request failed."));
    }
  };
  return <AccessShell title="Resend verification" description={sent ? "If an eligible account exists, a new verification link has been sent." : "Enter your email to request a fresh verification link."} icon={Mail}>{sent ? <Link className="text-sm text-[var(--cc-gold)]" to="/signin">Return to sign in</Link> : <form onSubmit={submit} className="space-y-5"><label className="block text-sm">Email<input className={`${fieldClass} mt-2`} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label><button className="btn-gold w-full rounded py-3 text-sm">Send verification link</button></form>}</AccessShell>;
}

export function ConsumerVerifyEmail() {
  const [state, setState] = useState("working");
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = fragmentToken();
    if (!token) { setState("invalid"); return; }
    consumerApi.post("/consumer/auth/verify-email", { token }).then(() => setState("done")).catch(() => setState("invalid"))
      .finally(() => { delete window.__CC_CONSUMER_ACTION_TOKEN__; });
  }, []);
  return <AccessShell title={state === "done" ? "Email verified" : state === "invalid" ? "Verification unavailable" : "Verifying your email"} description={state === "done" ? "Your account is active and ready to use." : state === "invalid" ? "This verification link is invalid or has expired." : "Please wait while we verify this link."} icon={CheckCircle2}>{state !== "working" && <Link className="text-sm text-[var(--cc-gold)]" to="/signin">Continue to sign in</Link>}</AccessShell>;
}

export function ConsumerResetPassword() {
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [token] = useState(() => fragmentToken());
  const submit = async (event) => {
    event.preventDefault();
    try {
      await consumerApi.post("/consumer/auth/reset-password", { token, new_password: password });
      delete window.__CC_CONSUMER_ACTION_TOKEN__;
      setDone(true);
    } catch (error) {
      toast.error(apiError(error, "Password reset failed."));
    }
  };
  return <AccessShell title={done ? "Password reset" : "Choose a new password"} description={done ? "You can now sign in with your new password." : "Use a strong password of at least 12 characters."} icon={KeyRound}>{done ? <Link className="text-sm text-[var(--cc-gold)]" to="/signin">Continue to sign in</Link> : <form onSubmit={submit} className="space-y-5"><label className="block text-sm font-medium">New password<input className={`${fieldClass} mt-2`} type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(e) => setPassword(e.target.value)} /></label><button className="btn-gold w-full rounded py-3 text-sm" disabled={!token}>Reset password</button></form>}</AccessShell>;
}
